import config from 'constants/config'
import {
  ITEM_STATUS_COMPLETED,
  ITEM_STATUS_CANCELLED,
  ITEM_STATUS_FAILED,
  ORDER_STATUS_CANCELLED,
  ITEM_STATUS_CREATED,
  ITEM_STATUS_PENDING,
} from 'constants/cart'
import {
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_LAST_MINUTE,
  OFFER_TYPE_VILLA,
} from 'lib/../constants/offer'
import {
  isEmpty,
  last,
  max,
  min,
  nonNullable,
  sortBy,
  sum,
} from 'lib/array/arrayUtils'
import {
  isCoverGeniusEnabled,
  isCoverGeniusEndDateValid,
  isInsuranceStartDateValid,
} from 'lib/insurance/insuranceHelpers'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { isCruiseOffer } from 'lib/offer/offerTypes'
import type { SetRequired } from 'type-fest'
import { VendorsThatRequireTravellerDetails } from 'constants/tours'
import { getInsuranceDatesFromOrder } from './orderInsuranceUtils'
import { addDays, isFutureDateString } from 'lib/datetime/dateUtils'
import { airportTransfersUpdateRequired, isAirportTransferAfterToday, filterAirportTransfers } from 'components/Pages/MyEscapesPage/AirportTransfers/utils/airportTransferUtils'
import { isCharterCancellationPolicy } from 'lib/offer/cancellationPolicyUtils'
import { CHARTER } from 'constants/provider'
import type { OrderStatusLabels } from 'hooks/useOrderStatusLabels'

export function getLatestCompletedReservation(order: App.Order): SetRequired<App.OrderItem, 'reservation'> | undefined {
  // Filter for only the order items with completed, non-last-minute, reservations
  const completedReservations = order.items.filter((i): i is SetRequired<App.OrderItem, 'reservation'> =>
    !!i.reservation &&
    i.status === ITEM_STATUS_COMPLETED &&
    i.offer.type !== OFFER_TYPE_LAST_MINUTE,
  )
  if (completedReservations.length > 0) {
    const itemWithLatestResDate = max(completedReservations, item => item.reservation ? new Date(item.reservation.endDate).getTime() : new Date().getTime())
    return itemWithLatestResDate
  }

  return undefined
}

export function orderCanAddInsurance(order: App.Order, offer?: App.AnyOffer) {
  // Bundle and save doesn't support insurance yet
  if (order.items.some(i => i.offer.bundleOfferId)) return false
  // Order must not already have insurance (unless it's cancelled)
  if (order.insuranceItems.some(i => i.status !== ITEM_STATUS_CANCELLED)) return false
  // reservation must be in the future
  if (order.reserveForZeroDetails?.isActive) return false

  if (isCruiseOffer(offer)) {
    // past cruises orders doesn't contain passengers, in this case we don't want to add insurance
    return order.cruiseItems.every(item => !!item.passengers.length)
  }

  const datesFromOrder = getInsuranceDatesFromOrder(order)
  if (!datesFromOrder) return false
  const { startDate, endDate } = datesFromOrder

  if (!endDate || !isCoverGeniusEndDateValid(endDate) || !startDate || !isInsuranceStartDateValid(startDate)) return false

  return true
}

export function isItemCancelled(item: App.AnyOrderItem) {
  return item.status === ITEM_STATUS_CANCELLED
}

export function isCruiseItemCancelledOrFailed(item: App.OrderCruiseItem) {
  return item.status === ITEM_STATUS_CANCELLED || item.status === ITEM_STATUS_FAILED
}

export function canAddInsuranceToOrder(order?: App.Order, region: string = '', offer?: App.AnyOffer) {
  return isCoverGeniusEnabled(region) &&
    config.ADD_INSURANCE_AFTER_PURCHASE &&
    order &&
    orderCanAddInsurance(order, offer)
}

export function isOrderCancelled(order: App.Order) {
  const orderCancelled = order.status === ORDER_STATUS_CANCELLED
  const everyItemCancelled = order.items.every(isItemCancelled) &&
    order.bedbankItems.every(isItemCancelled) &&
    order.tourItems.every(isItemCancelled) &&
    order.cruiseItems.every(isCruiseItemCancelledOrFailed) &&
    order.experienceItems.every(isItemCancelled) &&
    order.flightItems.every(isItemCancelled) &&
    order.carHireItems.every(isItemCancelled) &&
    order.transferItems.every(isItemCancelled) &&
    order.customOfferItems.every(isItemCancelled) &&
    order.merchandiseItems.every(isItemCancelled) &&
    order.merchandiseShippingItems.every(isItemCancelled)

  return orderCancelled || everyItemCancelled
}

export function isPayAtPropertyOrder(order: App.Order) {
  return order.bedbankItems.some(item => item.paymentMethod === 'propertyCollect')
}

export function getCombinedItemIds(order: App.Order): Array<string> {
  return [
    order.items.map(i => i.id),
    order.giftCardItems.map(i => i.id),
    order.insuranceItems.map(i => i.id),
    order.addonItems.map(i => i.id),
    order.flightItems.map(i => i.id),
    order.bedbankItems.map(i => i.id),
    order.experienceItems.map(i => i.id),
    order.transferItems.map(i => i.id),
    order.merchandiseItems.map(i => i.id),
    order.merchandiseShippingItems.map(i => i.id),
  ].flat()
}

export function getDateRangeFromItems(items: Array<App.OrderItem>): { earliestCheckInDate: string, latestCheckOutDate: string } {
  const completeItemsWithDates = items.filter(item => item.status === 'completed' && item.reservation)
  const sortedItems = sortBy(completeItemsWithDates, item => item.reservation!.startDate, 'asc')
  return {
    earliestCheckInDate: sortedItems[0]?.reservation!.startDate,
    latestCheckOutDate: last(sortedItems)?.reservation!.endDate,
  }
}

// For each order, get the dates from all items of the order. Returns the earliest date for the order so that all orders can be sorted by each order's earliest date.
export function sortOrdersByDeparture(orders: Array<App.Order>) {
  return sortBy(orders, (order) => {
    const allOrderItemDates = nonNullable([
      ...order.items.filter(item => item.status !== 'cancelled').map(item => item.reservation?.startDate),
      ...order.bedbankItems.filter(item => item.status !== 'cancelled').map(item => item.checkIn),
      ...order.tourItems.filter(item => item.status !== 'cancelled').map(item => item.tour.startDate),
      ...order.experienceItems.filter(item => item.status !== 'cancelled').map(item => item.date ?? item.bookByDate),
      ...order.flightItems.filter(item => item.status !== 'cancelled').map(item => item.departureDate),
      ...order.transferItems.filter(item => item.status !== 'cancelled').map(item => item.date),
      ...order.carHireItems.filter(item => item.status !== 'cancelled' && item.status !== 'failed').map(item => item.reservation?.pickUp.date),
      ...order.cruiseItems.filter(item => !isCruiseItemCancelledOrFailed(item)).map(item => item.departureDate),
      // Lets assume merchandise items 'departure' is their maximum delivery date
      ...order.merchandiseItems.filter(item => item.status !== 'cancelled').map(item => addDays(new Date(order.createdAt), item.snapshot.seller.shipping.maxDays)),
    ]).map(date => moment(date))
    // None of the order items have dates, will be sorted to top of the list of orders
    if (isEmpty(allOrderItemDates)) return moment()

    return moment.min(allOrderItemDates)
  }, 'asc')
}

function getTotalOrderItemCount(order: App.Order) {
  return sum([
    order.items.length,
    order.giftCardItems.length,
    order.insuranceItems.length,
    order.addonItems.length,
    order.flightItems.length,
    order.bedbankItems.length,
    order.experienceItems.length,
    order.carHireItems.length,
    order.transferItems.length,
    order.merchandiseItems.length,
    order.merchandiseShippingItems.length,
    order.tourItems.length,
    order.cruiseItems.length,
    order.customOfferItems.length,
  ])
}

type StandaloneOrderType = 'flights' |
  'experiences' |
  'transfers' |
  'merchandise' |
  'hotel' |
  'tour' |
  'cruise' |
  'carHire'

export function getStandaloneOrderType(order: App.Order): StandaloneOrderType | undefined {
  const count = getTotalOrderItemCount(order)
  if (count === order.flightItems.length) return 'flights'
  if (count === order.experienceItems.length) return 'experiences'
  if (count === order.transferItems.length) return 'transfers'
  if (count === (order.merchandiseItems.length + order.merchandiseShippingItems.length)) return 'merchandise'
  if (count === (order.items.length + order.bedbankItems.length)) return 'hotel'
  if (count === order.tourItems.length) return 'tour'
  if (count === order.cruiseItems.length) return 'cruise'
  if (count === order.carHireItems.length) return 'carHire'

  return undefined
}

export function isStandaloneOrder(
  order: App.Order,
  type: StandaloneOrderType,
) {
  const standaloneType = getStandaloneOrderType(order)
  return standaloneType === type
}

export function isStandaloneFlightsOrder(order: App.Order): boolean {
  return isStandaloneOrder(order, 'flights')
}

export function isStandaloneMerchandiseOrder(order: App.Order): boolean {
  return isStandaloneOrder(order, 'merchandise')
}

export function haveOrdersBeenFetched(state: App.State, orderType: App.OrderListTypes = 'all') {
  return state.orders.ordersFetched.all || state.orders.ordersFetched[orderType]
}

export function areOrdersBeingFetched(state: App.State, orderType: App.OrderListTypes = 'all') {
  return state.orders.fetching.all || state.orders.fetching[orderType]
}

export function getOrderExperienceDateRange(order: App.Order) {
  const range = getDateRangeFromItems(order.items)
  return {
    from: range.earliestCheckInDate ? moment(range.earliestCheckInDate).add(-7, 'days').format(ISO_DATE_FORMAT) : undefined,
    to: range.latestCheckOutDate ? moment(range.latestCheckOutDate).add(7, 'days').format(ISO_DATE_FORMAT) : undefined,
  }
}

export function isBundleAndSaveOrder(order: App.Order) {
  return order.items.some(item => item.offer.bundleOfferId !== undefined)
}

export function isVillaOrder(order: App.Order) {
  return order.items.some(item => item.offer.type === OFFER_TYPE_VILLA)
}

export function isHotelOrder(order: App.Order) {
  return order.items.some(item => item.offer.type === OFFER_TYPE_HOTEL)
}

export function isLPCHotelOrder(order: App.Order) {
  return order.items.some(item => item.offer.type === OFFER_TYPE_ALWAYS_ON)
}

export function isSingleHotelOrder(order: App.Order) {
  return order?.items.length === 1 && isStandaloneOrder(order, 'hotel')
}

export function isFlightOrder(order: App.Order) {
  return order?.flightItems.length > 0
}

export function isHotelOrderItem(item: App.AnyOrderItem): item is App.OrderItem {
  if ('offer' in item) return item.offer.type === OFFER_TYPE_HOTEL
  return false
}

export function isLPCHotelOrderItem(item: App.AnyOrderItem): item is App.OrderItem {
  if ('offer' in item) return item.offer.type === OFFER_TYPE_ALWAYS_ON
  return false
}

export function isFlightOrderItem(item: App.AnyOrderItem): item is App.OrderFlightItem {
  if ('provider' in item && 'pnrId' in item) return true
  return false
}

export function isBedbankOrderItem(item: App.AnyOrderItem): item is App.OrderBedbankItem {
  if ('offer' in item) return item.offer.type === 'bedbank_hotel'
  return false
}

export function isTourV2OrderItem(item: App.AnyOrderItem): item is App.Tours.TourV2OrderItem {
  if ('offer' in item) return item.offer.type === 'tour_v2'
  return false
}

export function isCruiseV2OrderItem(item: App.AnyOrderItem): item is App.OrderCruiseItem {
  if ('cruiseOfferId' in item) return true
  return false
}

export function isInsuranceOrderItem(item: App.AnyOrderItem): item is App.OrderInsuranceItem {
  if ('insuranceType' in item) return true
  return false
}

export function isBookingProtectionOrderItem(item: App.AnyOrderItem): item is App.OrderBookingProtectionItem {
  if ('bookingReferenceId' in item && 'quoteId' in item) return true
  return false
}

export function isExperienceOrderItem(item: App.AnyOrderItem): item is App.OrderExperienceItem {
  return 'experienceId' in item
}

export function isTransferOrderItem(item: App.AnyOrderItem): item is App.OrderTransferItem {
  return 'option' in item && ['AIRPORT-TO-HOTEL', 'HOTEL-TO-AIRPORT'].includes(item.type)
}

export function isLuxPlusSubscriptionOrderItem(item: App.AnyOrderItem): item is App.OrderItemLuxPlusSubscription {
  return 'subscriptionId' in item
}

export function isMerchandiseOrderItem(item: App.AnyOrderItem): item is App.OrderMerchandiseItem {
  return 'variantId' in item
}

export function isMerchandiseShippingOrderItem(item: App.AnyOrderItem): item is App.OrderMerchndiseShippingItem {
  return 'shippingGroupId' in item
}

export function isTourExperienceItem(item: App.AnyOrderItem): item is App.Tours.TourV2OrderExperienceItem {
  return 'itemType' in item && item.itemType === 'tour_experience'
}

export function isOrderItem(item: App.AnyOrderItem | undefined): item is App.OrderItem {
  if (!item) {
    return false
  }

  if ('offer' in item) {
    return item.offer.type === 'hotel' ||
      item.offer.type === 'bundle_and_save' ||
      item.offer.type === 'rental' ||
      item.offer.type === 'tactical_ao_hotel' ||
      item.offer.type === 'last_minute_hotel'
  }
  return false
}

export function isOrderItemHotelPackage(
  pkg: App.OrderItemPackage | undefined,
): pkg is App.OrderItemHotelPackage {
  return !!pkg
}

export function getOrderLocation(order: App.Order) {
  let location: string | undefined

  if (order.items.length > 0) {
    const offer = order.items[0].offer
    location = [offer.property?.locationHeading, offer.property?.locationSubheading].filter(t => t).join(', ')
  } else if (order.bedbankItems.length > 0) {
    const offer = order.bedbankItems[0].offer
    location = [offer.property?.address?.city, offer.property?.address?.stateProvinceName].filter(t => t).join(', ')
  }

  return location
}

export function getOrderTravelDuration(order: App.Order) {
  const nonCancelledItems = order.items.filter(item => item.status !== ITEM_STATUS_CANCELLED)
  const travelDurations = [
    ...nonNullable((nonCancelledItems.length > 0 ? nonCancelledItems : order.items).map(item => ({
      startDate: item.reservation?.startDate,
      endDate: item.reservation?.endDate,
    }))),
    // Ideally, to accurately determine the "active" and "earliest" bedbank item (especially since LPP change dates can be moved earlier or later),
    // TODO: we should sort these items using their createdAt timestamp. However, this logic currently does not have access to createdAt.
    ...nonNullable(order.bedbankItems.filter(bedbankItem => bedbankItem.status !== 'cancelled').map(bedbankItem => ({
      startDate: bedbankItem.checkIn.format('YYYY-MM-DD'),
      endDate: bedbankItem.checkOut.format('YYYY-MM-DD'),
    }))),
    ...nonNullable(order.cruiseItems.map(cruiseItem => ({
      startDate: cruiseItem.departureDate,
      endDate: cruiseItem.arrivalDate,
    }))),
    ...nonNullable(order.flightItems.map(flightItem => ({
      startDate: flightItem.departureDate,
      endDate: flightItem.arrivalDate,
    }))),
    ...nonNullable(order.tourItems.map(tourItem => ({
      startDate: tourItem.tour.startDate,
      endDate: tourItem.tour.endDate,
    }))),
    ...nonNullable(order.experienceItems.map(experienceItem => ({
      startDate: experienceItem.date,
      endDate: experienceItem.date,
    }))),
    ...nonNullable(order.carHireItems.map(carHireItem => ({
      startDate: carHireItem.reservation?.pickUp.date,
      endDate: carHireItem.reservation?.dropOff.date,
    }))),
    ...nonNullable(order.customOfferItems.map(customOfferItem => ({
      startDate: customOfferItem.custom_offer.items[0].start_date,
      endDate: customOfferItem.custom_offer.items[0].end_date,
    }))),
  ]
  return {
    earliest: min(nonNullable(travelDurations.map(item => item.startDate ? item.startDate : undefined)), date => new Date(date)),
    latest: max(nonNullable(travelDurations.map(item => item.endDate ? item.endDate : undefined)), date => new Date(date)),
  }
}

export function getAllOrderItems(order: App.Order): Array<App.AnyOrderItem> {
  return [
    ...order.items,
    ...order.bedbankItems,
    ...order.experienceItems,
    ...order.transferItems,
    ...order.tourItems,
    ...order.tourExperienceItems,
    ...order.carHireItems,
    ...order.cruiseItems,
    ...order.customOfferItems,
    ...order.flightItems,
    ...order.addonItems,
    ...order.bookingProtectionItems,
    ...order.merchandiseItems,
    ...order.merchandiseShippingItems,
  ]
}

function isDateToBeSelected(order: App.Order): boolean {
  const allItems = getAllOrderItems(order)

  const isDateToBeSelected = allItems.some((item) => {
    const orderItem = isOrderItem(item) && item.canBookDates
    const experienceItem = isExperienceOrderItem(item)
    const canBook =
      (orderItem || experienceItem) &&
      !isExpiredBNBL(item)

    return canBook && item.status === 'awaiting_dates'
  })
  return isDateToBeSelected
}

export function isExpiredBNBL(item: App.OrderItem | App.OrderExperienceItem) {
  if (item.status !== 'awaiting_dates') {
    return false
  }

  const bookByDate = isOrderItem(item) ? item.offer.bookByDate : item.bookByDate
  const travelToDate = isOrderItem(item) ? item.offer.travelToDate : item.expirationDate

  const isBookByDateExpired = !!bookByDate && !isFutureDateString(bookByDate)
  const isTravelToDateExpired = !!travelToDate && !isFutureDateString(travelToDate)

  return isBookByDateExpired || isTravelToDateExpired
}

function orderHasExpiredBnbl(order: App.Order) {
  const allItems = getAllOrderItems(order)
  return allItems.some(item => isOrderItem(item) && isExpiredBNBL(item))
}

function isAirportTransferInformationNeeded(order: App.Order, experiences?: Record<string, App.ExperienceOffer>) {
  const allItems = getAllOrderItems(order)
  const allNonTransferItems = allItems.filter(item => !isTransferOrderItem(item))
  const anyNonTransferItemPending = allNonTransferItems.some(item => item.status === ITEM_STATUS_CREATED || item.status === ITEM_STATUS_PENDING)

  // Other items have preference
  if (anyNonTransferItemPending) return false

  const allTransferItems = filterAirportTransfers(allItems.filter(item => isTransferOrderItem(item)))
  return airportTransfersUpdateRequired(allTransferItems, experiences) && isAirportTransferAfterToday(allTransferItems)
}

export type OrderStatusType = 'success' | 'warning' | 'critical' | ''

export type OrderStatusKey =
  | 'bookingLocked'
  | 'datesToBeSelected'
  | 'airportTransferInformationNeeded'
  | 'travellerDetailsNeeded'
  | 'scheduledToBeSent'
  | 'reservationCancelled'
  | 'bookingCancelled'
  | 'bookingPending'
  | 'reservationConfirmed'
  | 'bookingConfirmed'

function getActionNeeded(
  order: App.Order,
  isCancelled: boolean,
  labels: OrderStatusLabels,
  travellerDetails?: App.TourOrderTravellerDetails,
  experiences?: Record<string, App.ExperienceOffer>,
): { text: string, key: OrderStatusKey } | null {
  const { canEdit } = order.accessDetails
  if (!isCancelled) {
    if (order.locked) {
      return { text: labels.bookingLocked, key: 'bookingLocked' }
    }

    const allItems = getAllOrderItems(order)
    if (isDateToBeSelected(order)) {
      return { text: labels.datesToBeSelected, key: 'datesToBeSelected' }
    } else if (isAirportTransferInformationNeeded(order, experiences)) {
      return { text: labels.airportTransferInformationNeeded, key: 'airportTransferInformationNeeded' }
    } else {
      const travellerDetailsRequired = VendorsThatRequireTravellerDetails.has((allItems[0] as App.OrderItem).vendorId) && travellerDetails &&
        travellerDetails.total_submitted < travellerDetails.total_travellers
      if (canEdit && travellerDetailsRequired) {
        return { text: labels.travellerDetailsNeeded, key: 'travellerDetailsNeeded' }
      }
    }
  }
  return null
}

export function getOrderStatus(
  order: App.Order,
  isCancelled: boolean,
  labels: OrderStatusLabels,
  travellerDetails?: App.TourOrderTravellerDetails,
  experiences?: Record<string, App.ExperienceOffer>,
): { text: string, type: OrderStatusType, key?: OrderStatusKey } {
  if (order.orderSuccess && (order.orderGiftStatus && order.orderGiftStatus !== 'redeemed')) {
    return { text: labels.scheduledToBeSent, type: 'success', key: 'scheduledToBeSent' }
  }

  const actionNeeded = getActionNeeded(order, isCancelled, labels, travellerDetails, experiences)
  const hasExpiredBnbl = orderHasExpiredBnbl(order)

  if (actionNeeded) {
    return { text: actionNeeded.text, type: 'warning', key: actionNeeded.key }
  }

  if (hasExpiredBnbl) {
    return { text: '', type: '' }
  }

  if (isCancelled) {
    const text = order.reserveForZeroDetails ? labels.reservationCancelled : labels.bookingCancelled
    const key: OrderStatusKey = order.reserveForZeroDetails ? 'reservationCancelled' : 'bookingCancelled'
    return { text, type: 'critical', key }
  }

  if (order.status === 'pending') {
    return { text: labels.bookingPending, type: 'warning', key: 'bookingPending' }
  }

  if (order.orderSuccess) {
    if (order.reserveForZeroDetails?.isActive) {
      return { text: labels.reservationConfirmed, type: 'success', key: 'reservationConfirmed' }
    }

    return { text: labels.bookingConfirmed, type: 'success', key: 'bookingConfirmed' }
  }

  return { text: '', type: '' }
}

export function isBedbankOrder(order: App.Order): boolean {
  return order.bedbankItems.length > 0
}

interface AccommodationItem {
  propertyId: string
  checkIn: string
  checkOut: string
}

export function isSingleAccommodationInOrder(order: App.Order | undefined): boolean {
  if (!order) {
    return false
  }

  const hasHotelItems = isHotelOrder(order)
  const hasBedbankItems = isBedbankOrder(order)
  const hasLPCHotelItems = isLPCHotelOrder(order)

  // Only one accommodation type supported (hotel OR bedbank OR LPC), not a mix
  const accommodationTypesCount = [hasHotelItems, hasBedbankItems, hasLPCHotelItems].filter(Boolean).length

  if (accommodationTypesCount !== 1) {
    return false
  }

  if (!hasHotelItems && !hasBedbankItems && !hasLPCHotelItems) {
    return false
  }

  const accommodationItems: Array<AccommodationItem> = []

  if (hasHotelItems || hasLPCHotelItems) {
    order.items.forEach(item => {
      if ((isHotelOrderItem(item) || isLPCHotelOrderItem(item)) && isOrderItemHotelPackage(item.package) && item.reservation) {
        const hotelPackage = item.package
        if (hotelPackage.propertyId) {
          accommodationItems.push({
            propertyId: hotelPackage.propertyId,
            checkIn: item.reservation.startDate,
            checkOut: item.reservation.endDate,
          })
        }
      }
    })
  }

  if (hasBedbankItems) {
    order.bedbankItems.forEach(item => {
      accommodationItems.push({
        propertyId: item.propertyId,
        checkIn: item.checkIn.format(ISO_DATE_FORMAT),
        checkOut: item.checkOut.format(ISO_DATE_FORMAT),
      })
    })
  }

  if (accommodationItems.length === 0) {
    return false
  }

  const uniquePropertyIds = new Set(accommodationItems.map(item => item.propertyId))
  if (uniquePropertyIds.size > 1) {
    return false
  }

  const uniqueDateRanges = new Set(accommodationItems.map(item => `${item.checkIn}-${item.checkOut}`))
  if (uniqueDateRanges.size > 1) {
    return false
  }

  return true
}

export function getCustomOfferPackageTotal(order: App.Order): number | undefined {
  if (isCustomOfferPackageOrder(order)) {
    return (order.customOfferItems[0].custom_offer.payment_metadata?.final_amount_customer_display ?? 0) / 100
  }
  return undefined
}

export function isCustomOfferPackageOrder(order: App.Order) {
  return order.customOfferItems?.[0]?.custom_offer?.items[0]?.type === 'custom-offer-package'
}

export function canViewHiddenCustomOfferItems(order: App.Order, isSpoofed = false) {
  return isSpoofed || order.accessDetails.role === 'GROUP_CUSTOM_OFFER_VIEWER'
}

export function isCustomOfferItemVisible(order: App.Order, item: App.OrderCustomOfferItem, isSpoofed = false) {
  return canViewHiddenCustomOfferItems(order, isSpoofed) || !item.custom_offer?.hidden_from_customer
}

export function hasVisibleCustomOfferItems(order: App.Order, isSpoofed = false) {
  return order.customOfferItems.some(item => isCustomOfferItemVisible(order, item, isSpoofed))
}

export function hasSpoofVisibleHiddenCustomOfferItems(order: App.Order, isSpoofed = false) {
  return isSpoofed && order.customOfferItems.some(item => item.custom_offer?.hidden_from_customer)
}

export function shouldHideCustomOfferPricing(order: App.Order) {
  return order.customOfferItems.length > 0 && order.accessDetails.role === 'GROUP_CUSTOM_OFFER_VIEWER'
}

export function getIsUnpaidCustomOffer(order: App.Order) {
  return order.customOfferItems.length > 0 && order.status === 'pending'
    // pending status is set on the item if it is awaiting manual confirmation due to vendor_is_non_refundable
    && !order.customOfferItems.some((item) => item.status === 'pending' && item.custom_offer?.payment_metadata?.vendor_is_non_refundable)
}

export function getIsCharterBundleOrder(order: App.Order) {
  // Check if order has hotel items
  const hasItems = order.items.length > 0

  // Check if flight provider is 'charter'
  const hasCharterProvider = order.flightItems.some(flightItem => flightItem.provider === CHARTER)

  // Check if cancellation policy includes 'charter' keyword
  const hasCharterCancellationPolicy = order.items.some(item =>
    item.reservation?.cancellationPolicies?.some(isCharterCancellationPolicy),
  )

  return (hasCharterProvider || hasCharterCancellationPolicy) && hasItems
}
